<template>
  <div class="login-page">
    <div class="login-bg">
      <div class="bg-orb orb-1"></div>
      <div class="bg-orb orb-2"></div>
      <div class="bg-grid"></div>
    </div>

    <div class="login-container">
      <div class="login-card">
        <!-- Brand -->
        <div class="brand">
          <div class="brand-mark">
            <span>AB</span>
          </div>
          <div class="brand-text">
            <h1>ABPoint</h1>
            <p>Système de Gestion RH</p>
          </div>
        </div>

        <div class="divider"></div>

        <!-- Form -->
        <form @submit.prevent="login" class="login-form">
          <h2>Connexion</h2>
          <p class="form-subtitle">Entrez vos identifiants pour accéder</p>

          <div class="field">
            <label>Nom d'utilisateur</label>
            <div class="input-wrap">
              <svg class="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
              <input v-model="username" type="text" placeholder="Votre identifiant" required autocomplete="username" />
            </div>
          </div>

          <div class="field">
            <label>Mot de passe</label>
            <div class="input-wrap">
              <svg class="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              <input v-model="password" :type="showPass ? 'text' : 'password'" placeholder="Votre mot de passe" required autocomplete="current-password" />
              <button type="button" class="toggle-pass" @click="showPass = !showPass">
                <svg v-if="!showPass" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              </button>
            </div>
          </div>

          <div v-if="errorMessage" class="error-box">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ errorMessage }}
          </div>

          <button type="submit" class="login-btn" :class="{ loading: isLoading }">
            <span v-if="!isLoading">Se connecter</span>
            <span v-else class="spinner"></span>
            <svg v-if="!isLoading" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </form>

        <p class="footer-note">© 2026 ABPoint · Tous droits réservés</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const username = ref('');
const password = ref('');
const errorMessage = ref('');
const isLoading = ref(false);
const showPass = ref(false);

const login = async () => {
  errorMessage.value = '';
  isLoading.value = true;
  try {
    const response = await fetch(`/api-pointage/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.value.toLowerCase().trim(), password: password.value }),
      credentials: 'include',
    });
    if (response.ok) {
      let data = await response.json();
      const privilage = data.privilage;
      window.dispatchEvent(new Event('auth-changed'));
      const home = privilage === 'caissier' ? '/cashier' : privilage === 'employe' ? '/mes-avances' : '/';
      router.replace(home);
    } else {
      const errorData = await response.json();
      errorMessage.value = errorData.error || errorData.message || 'Identifiants invalides';
    }
  } catch (error) {
    errorMessage.value = 'Connexion impossible. Réessayez.';
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e8eef7 100%);
  position: relative;
  overflow: hidden;
  padding: 20px;
}

.login-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.bg-orb {
  display: none;
}

.orb-1 {
  width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(26,86,219,0.3) 0%, transparent 70%);
  top: -200px; left: -100px;
  animation: driftOrb 8s ease-in-out infinite alternate;
}

.orb-2 {
  width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(245,158,11,0.2) 0%, transparent 70%);
  bottom: -100px; right: -50px;
  animation: driftOrb 10s ease-in-out infinite alternate-reverse;
}

.bg-grid { display: none; }

@keyframes driftOrb {
  from { transform: translate(0, 0) scale(1); }
  to { transform: translate(40px, 30px) scale(1.1); }
}

.login-container {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 390px;
}

.login-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 28px;
  box-shadow: var(--shadow-md);
  animation: slideUp 0.25s ease;
}

.brand {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
}

.brand-mark {
  width: 42px; height: 42px;
  background: #1a56db;
  border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: none;
  flex-shrink: 0;
}

.brand-mark span {
  font-family: 'DM Sans', sans-serif;
  font-size: 17px; font-weight: 800;
  color: #fff; letter-spacing: 0.02em;
}

.brand-text h1 {
  font-size: 16px; font-weight: 800;
  color: #0f172a; margin: 0; letter-spacing: 0.02em;
}

.brand-text p {
  font-size: 12px; color: rgba(15,23,42,0.6);
  margin: 0; font-weight: 500;
}

.divider {
  height: 1px;
  background: var(--border);
  margin-bottom: 20px;
}

.login-form h2 {
  font-size: 20px; color: #0f172a;
  margin-bottom: 4px;
}

.form-subtitle {
  font-size: 13.5px;
  color: rgba(15,23,42,0.55);
  margin-bottom: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 16px;
}

.field label {
  font-size: 12px; font-weight: 600;
  color: rgba(15,23,42,0.65);
  text-transform: uppercase; letter-spacing: 0.06em;
}

.input-wrap {
  position: relative;
  display: flex; align-items: center;
}

.input-wrap input {
  background: rgba(26,86,219,0.06);
  border: 1.5px solid rgba(26,86,219,0.15);
  border-radius: 10px;
  padding: 11px 14px 11px 42px;
  color: #0f172a; font-size: 14px; width: 100%;
  transition: all 0.2s ease;
}

.input-wrap input::placeholder { color: rgba(15,23,42,0.4); }

.input-wrap input:focus {
  outline: none;
  border-color: #1a56db;
  background: rgba(26,86,219,0.12);
  box-shadow: 0 0 0 3px rgba(26,86,219,0.1);
}

.field-icon {
  position: absolute; left: 13px;
  width: 16px; height: 16px;
  color: rgba(15,23,42,0.5);
  pointer-events: none;
}

.toggle-pass {
  position: absolute; right: 12px;
  background: none; border: none; cursor: pointer;
  color: rgba(15,23,42,0.5); padding: 4px;
  display: flex; align-items: center;
  transition: color 0.2s;
}
.toggle-pass:hover { color: rgba(15,23,42,0.8); }
.toggle-pass svg { width: 16px; height: 16px; }

.error-box {
  display: flex; align-items: center; gap: 8px;
  background: rgba(239,68,68,0.1);
  border: 1px solid rgba(239,68,68,0.2);
  border-radius: 8px; padding: 10px 14px;
  color: #dc2626; font-size: 13px;
  margin-bottom: 4px;
}
.error-box svg { width: 15px; height: 15px; flex-shrink: 0; }

.login-btn {
  width: 100%;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 13px 20px; margin-top: 4px;
  background: linear-gradient(135deg, #1a56db 0%, #4f8ef7 100%);
  color: #fff; border: none; border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px; font-weight: 700;
  cursor: pointer; letter-spacing: 0.01em;
  box-shadow: 0 6px 24px rgba(26,86,219,0.3);
  transition: all 0.2s ease;
}

.login-btn svg { width: 17px; height: 17px; }

.login-btn:hover:not(.loading) {
  transform: translateY(-2px);
  box-shadow: 0 10px 32px rgba(26,86,219,0.4);
}

.login-btn.loading { opacity: 0.7; cursor: not-allowed; }

.spinner {
  width: 18px; height: 18px;
  border: 2.5px solid rgba(26,86,219,0.3);
  border-top-color: #1a56db;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.footer-note {
  text-align: center; margin-top: 24px;
  font-size: 11.5px; color: rgba(15,23,42,0.4);
}
</style>
